import { inject, InjectionToken, Pipe, PipeTransform } from '@angular/core';
import { formatDate } from '@angular/common';
import { BazisLocaleService } from '@bazis/shared/services/locale.service';
import { DATETIME_LOCALE } from '@bazis/configuration.service';

export const DATETIME_LOCALE_DATA = new InjectionToken<any>('', {
    providedIn: 'root',
    factory: () => DATETIME_LOCALE,
});
/**
 * Formats a date/time value using the current app locale and a format preset
 * from `DATETIME_LOCALE`. Falls back to a raw Angular date pattern if the key
 * is not found. Supports optional UTC formatting.
 *
 * How it works:
 * - Gets the active locale from `BazisLocaleService`.
 * - Looks up a preset by key in `DATETIME_LOCALE_DATA` for that locale.
 * - Uses Angular's `formatDate` to format the value (optionally as UTC).
 * - Pipe is `pure: false` to react to locale changes.
 *
 * Dependencies:
 * - `BazisLocaleService` (via `inject()`): provides the current locale.
 * - `DATETIME_LOCALE_DATA` InjectionToken: by default, uses `DATETIME_LOCALE` from
 *   `BazisConfigurationService`.
 *
 * ### Customization in Your Project
 * To override date/time formats for your app, follow this flow:
 *
 * 1. **Override `DATETIME_LOCALE` in your project configuration service**
 *    (e.g., in `src/app/configuration.service.ts`):
 *    ```ts
 *    // src/app/configuration.service.ts
 *    export const DATETIME_LOCALE = {
 *      ru: { ...DATETIME_LOCALE.ru, shortDate: 'dd.MM.yyyy' },
 *      en: { ...DATETIME_LOCALE.en, shortDate: 'MM/dd/yyyy' },
 *    };
 *    ```
 *
 * 2. **Import your custom value and provide it in your app bootstrap**
 *    ```ts
 *    import { bootstrapApplication } from '@angular/platform-browser';
 *    import { DATETIME_LOCALE_DATA } from '@bazis/shared/pipes/local-date.pipe';
 *    import { DATETIME_LOCALE } from './app/configuration.service';
 *
 *    bootstrapApplication(AppComponent, {
 *      providers: [
 *        { provide: DATETIME_LOCALE_DATA, useValue: DATETIME_LOCALE },
 *      ],
 *    });
 *    ```
 *
 * This approach keeps your configuration clean and maintainable—override the locale object in your config service, then import and provide it for the pipe.
 *
 * Signature:
 * `transform(value: any, format: string, isUtc?: boolean, locale?: string): string`
 *
 * Parameters:
 * - `value`: Date, ISO string, timestamp, etc. Empty value returns empty string.
 * - `format`: Preset key (e.g., `'shortDateTime'`) or raw Angular format pattern.
 * - `isUtc`: If true, formats as UTC (timezone `+00:00`). Default: false.
 * - `locale`: Locale code. Default: current locale from `BazisLocaleService`.
 *
 * Examples (Angular v20+):
 * ```html
 * <!-- Basic -->
 * {{ order.$snapshot.dt_created | localDate:'mediumDate' }}
 *
 * <!-- UTC formatting -->
 * {{ order.$snapshot.dt_created | localDate:'shortDateTime':true }}
 *
 * <!-- Custom pattern fallback (uses Angular date pattern directly) -->
 * {{ order.$snapshot.dt_created | localDate:'yyyy-MM-dd HH:mm' }}
 *
 * ```
 */
@Pipe({
    name: 'localDate',
    pure: false,
    standalone: true,
})
export class LocalDatePipe implements PipeTransform {
    protected localeService = inject(BazisLocaleService);

    protected datetimeLocale = inject(DATETIME_LOCALE_DATA);

    /**
     * Format a date/time value by locale and preset key.
     *
     * @param value Date, ISO string, number timestamp, etc.
     * @param format Preset key from `DATETIME_LOCALE` or an Angular date pattern.
     * @param isUtc When true, formats value as UTC. Default: false.
     * @param locale Locale code. Default: current from `BazisLocaleService`.
     * @returns Formatted date/time string. Empty string when input is empty.
     */
    transform(value: any, format: string, isUtc = false, locale = this.localeService.locale) {
        if (!value) {
            return '';
        }

        let finalFormat = this.datetimeLocale[locale][format] || format || 'shortDate';
        const timezone = isUtc ? '+00:00' : '';
        return formatDate(value, finalFormat, locale, timezone);
    }
}
